type ProfileSnapshotPhase = 'snapshot_loading_started' | 'snapshot_ready' | 'snapshot_failed';
type ProfileSnapshotFailureReason = 'transport' | 'decode' | 'invalid_snapshot';

export interface ProfileSnapshotEvidence {
  readonly at: string;
  readonly phase: ProfileSnapshotPhase;
  readonly requestOrdinal: number;
  readonly walletCount?: number;
  readonly positionCount?: number;
  readonly completePoolCount?: number;
  readonly incompletePoolCount?: number;
  readonly streamSequence?: string;
  readonly reason?: ProfileSnapshotFailureReason;
}

export interface ProfileValuationEvidence {
  readonly at: string;
  readonly positionCount: number;
  readonly pricedPositionCount: number;
  readonly unrealizedAvailableCount: number;
  readonly dustPositionCount: number;
  readonly holdingsAvailable: boolean;
  readonly cashAvailable: boolean;
  readonly totalAvailable: boolean;
}

export interface ProfileWireHandoffEvidence {
  readonly at: string;
  readonly phase: 'wallet_interest_ready';
  readonly walletCount: number;
}

export type ProfileReconciliationEvidenceReason =
  | 'wallet_position_event'
  | 'wallet_quote_balance_event'
  | 'lifecycle_event'
  | 'resolution_event'
  | 'identity_change'
  | 'payload_incomplete'
  | 'sequence_gap'
  | 'reconnect'
  | 'bounded_replay_failed'
  | 'wallet_set_changed';

export interface ProfileReconciliationEvidence {
  readonly at: string;
  readonly phase: 'scheduled' | 'coalesced' | 'completed' | 'failed';
  readonly reason: ProfileReconciliationEvidenceReason;
  readonly pending: boolean;
  readonly failureKind?: string;
}

export type ProfileObservabilityMessage =
  | 'profile_valuation.positive_positions_without_rate_filters'
  | 'profile_valuation.filter_install_failed'
  | 'profile_valuation.event_payload_incomplete'
  | 'profile_valuation.event_identity_invalid'
  | 'profile_valuation.sequence_gap'
  | 'profile_valuation.reconciliation_scheduled';

export interface ProfileObservabilityEvidence {
  readonly at: string;
  readonly message: ProfileObservabilityMessage;
  readonly reason?: string;
  readonly positivePositionCount?: number;
  readonly rateFilterCount?: number;
  readonly lifecycleFilterCount?: number;
}

export interface ProfilePortfolioEvidenceSnapshot {
  readonly profileSnapshots: readonly ProfileSnapshotEvidence[];
  readonly profileWireHandoffs: readonly ProfileWireHandoffEvidence[];
  readonly profileValuations: readonly ProfileValuationEvidence[];
  readonly profileReconciliations: readonly ProfileReconciliationEvidence[];
  readonly profileObservability: readonly ProfileObservabilityEvidence[];
}

const RING_CAP = 200;
let profileSnapshots: readonly ProfileSnapshotEvidence[] = [];
let profileWireHandoffs: readonly ProfileWireHandoffEvidence[] = [];
let profileValuations: readonly ProfileValuationEvidence[] = [];
let profileReconciliations: readonly ProfileReconciliationEvidence[] = [];
let profileObservability: readonly ProfileObservabilityEvidence[] = [];

export function recordProfileSnapshot(entry: Omit<ProfileSnapshotEvidence, 'at'>): void {
  profileSnapshots = append(profileSnapshots, { at: now(), ...entry });
}

export function recordProfileWireHandoff(entry: Omit<ProfileWireHandoffEvidence, 'at'>): void {
  profileWireHandoffs = append(profileWireHandoffs, { at: now(), ...entry });
}

export function recordProfileValuation(entry: Omit<ProfileValuationEvidence, 'at'>): void {
  profileValuations = append(profileValuations, { at: now(), ...entry });
}

export function recordProfileReconciliation(
  entry: Omit<ProfileReconciliationEvidence, 'at'>,
): void {
  profileReconciliations = append(profileReconciliations, { at: now(), ...entry });
}

export function recordProfileObservability(entry: Omit<ProfileObservabilityEvidence, 'at'>): void {
  profileObservability = append(profileObservability, { at: now(), ...entry });
}

export function profilePortfolioEvidenceSnapshot(): ProfilePortfolioEvidenceSnapshot {
  return {
    profileSnapshots: [...profileSnapshots],
    profileWireHandoffs: [...profileWireHandoffs],
    profileValuations: [...profileValuations],
    profileReconciliations: [...profileReconciliations],
    profileObservability: [...profileObservability],
  };
}

function append<T>(ring: readonly T[], entry: T): readonly T[] {
  return [...ring, entry].slice(-RING_CAP);
}

function now(): string {
  return new Date().toISOString();
}
